import { z } from 'zod'
import { Answer, Event } from './answer.js'
import { Block, CompartimentId, DossierId, InstantieId, PublicatieId, RevisieId, UitvraagRef, FieldId } from './id.js'
import { CriterionOutcome, Laag } from './laag.js'
import { Moment, Person } from './moment.js'

/**
 * Het document dat een bouwblok op het dossier achterlaat, als één stand (D-37, D-72).
 *
 * Drie dingen op de wortel en niet meer. `$stand` is de aanduiding van de stand die dit document
 * is: onze `RevisieId` bij een eigen blok, een stabiele en binnen dat blok unieke aanduiding bij
 * een blok buiten dit product (D-72). `compartimenten` houdt de velden die de inrichter zelf aan
 * het formulier heeft toegevoegd, op `compartimenten.<compartiment-id>.<veld-id>` (D-69). Al het
 * overige is het gestructureerde deel, zoals het contract van dat blok het beschrijft.
 *
 * **Een periode staat er niet op**, en dat is geen omissie. Elk document is één stand, en een
 * stand heeft geen periode op documentniveau; de periode staat in de items van een reeks, als een
 * eigenschap met `rol: periode` (D-73). De reeks die R-62 vraagt bestaat dus niet in één document
 * maar ontstaat uit de lijst standen.
 *
 * **Het gestructureerde deel wordt hier niet getoetst.** Dat gebeurt tegen het contract van het
 * blok en niet tegen dit schema: het contract leeft buiten deze definitie (D-37) en per blok
 * anders. Wat hier staat is de vorm die elk blokdocument heeft, wat er ook in staat; de toets aan
 * het contract hoort aan de grens waar een document binnenkomt.
 */
export const BlockDocument = z.looseObject({
  /** Vrij van vorm en niet leeg: een eigen blok levert een `RevisieId`, een extern blok zijn eigen aanduiding. */
  $stand: z.string().min(1),
  compartimenten: z.record(CompartimentId, z.record(z.string().min(1), z.unknown())).optional(),
})
export type BlockDocument = z.infer<typeof BlockDocument>

/**
 * Eén stand van één bouwblok, zoals het processysteem hem bij het uitzetten noemt (R-201, D-72).
 *
 * Voor een eigen bouwblok een verwijzing naar de revisie, voor een bouwblok buiten dit product het
 * document zelf. Dat verschil is er met opzet: een revisie lost dit product zelf op uit de eigen
 * opslag, en dan maken onze gegevens geen rondje door een ander systeem waarna niet meer
 * aantoonbaar is dat het nog hetzelfde document is. Een extern document heeft dat pad niet en gaat
 * daarom mee (besluit 6 van M7 opdracht 9).
 */
export const BlockState = z.union([RevisieId, BlockDocument])
export type BlockState = z.infer<typeof BlockState>

/**
 * Per bouwblok de lijst standen waarop de instantie rust, oudste eerst (R-201, R-62, R-56).
 *
 * **Dit is de bron; `DossierDocuments` hieronder is de afgeleide.** Wat het processysteem bij het
 * uitzetten noemt is deze lijst; de store lost de verwijzingen op en materialiseert het
 * blokdocument uit de revisie en de bevroren definitie (M8.4). **Die afleiding hoort in de store
 * en niet hier**: dit pakket heeft Zod als enige dependency en kent geen opslag.
 *
 * **Het toetsen van een extern document aan het contract van zijn blok gebeurt niet in de store,
 * maar aan de grens waar zo'n document binnenkomt (`apps/koppelvlak/src/aanmaken.ts`, M8.5).**
 * Deze zin zei tot M8.5 nog "de store... toetst een extern document aan het contract van zijn
 * blok"; dat was een vooruitgrijpende aanname uit besluit 6 van M7 opdracht 9, geschreven vóórdat
 * er een aanroeper of een contractbron was om het aan te toetsen. Bij het bouwen bleek de
 * opslagdienst zelf geen registry of contract te bezitten en `Publicatie` draagt er met opzet
 * geen mee; het contract per blok komt dus van de koppelvlakdienst, die aanmaken niet opnieuw kan
 * proberen (`form_instance_lijnwortel`) en daarom vóór het aanmaken toetst, dezelfde reden als bij
 * het adres van de publicatie. De pure toetsfunctie zelf (`toetsBlokdocument`, `contracttoets.ts`)
 * staat wél in dit pakket, als sibling van `bouwBlokdocument`: ze is I/O-vrij en hoort dus hier,
 * ook al ligt de beslissing wanneer ze wordt aangeroepen bij de koppelvlakdienst.
 *
 * Wat het formulier met de lijst doet: een verwijzing toont de laatste stand, want dat is de stand
 * die geldt (R-54, R-56); een reeks toont de items van de reeksdragende eigenschap uit alle standen,
 * aaneengeregen en oudste stand eerst, en de rol per eigenschap bepaalt de groepering (R-62, D-73).
 * Dat is de plek waar de reeks ontstaat: niet in één document, want elk document is één stand, maar
 * in de lijst.
 *
 * Op de instantie zeggen een lege lijst en een blok dat er niet in staat hetzelfde: er ligt niets.
 * De pod verzint dan niets en levert `onbeschikbaar` (R-61, R-63, besluit 4 van M6.0). Op het
 * aanmaakverzoek van een opvolger niet: daar erft een blok dat het verzoek niet noemt de lijst van
 * de voorganger, en laat een lege lijst het blok vervallen (`statesForSuccessor` in
 * `apps/store/src/instanties.ts`, R-201, R-63).
 *
 * **Op het aanmaakverzoek de lijst zoals het processysteem hem noemt, op de instantie de opgeloste
 * lijst.** Bij het aanmaken vervangt de store elke `RevisieId` door het blokdocument dat hij eruit
 * bouwt (`losDossierstandenOp`), in dezelfde orde en met evenveel standen per blok. Dat document
 * draagt de revisie als `$stand` (D-72), dus welke stand het is blijft zichtbaar. Zo heeft de pod
 * de hele lijst opgelost en hoeft hij nooit een andere instantie op te zoeken (M9). Een instantie
 * die is aangemaakt voordat de store dit deed draagt hier nog de lijst zoals hij binnenkwam; de
 * pod valt dan per blok terug op `dossierdocumenten` (`statesOfInstantie` in de engine). Vandaar
 * dat de vorm `BlockState` blijft en niet `Blokdocument` wordt: een oude rij moet nog te lezen zijn.
 *
 * **Dit landde in B4b, met de kolom en de schrijfweg in `POST /v1/instanties` in dezelfde
 * opdracht.** De lijst hoort op de instantie thuis, want R-62 rijgt de items van alle standen
 * aaneen en dat kan niet uit `dossierdocumenten`, dat er per blok maar een draagt. `Instantie` is
 * een `strictObject`, dus een pod die op het oude schema is bevroren zou een nieuwe sleutel met
 * `unrecognized_keys` weigeren; `apps/store/src/schema.test.ts` bewaakt dat de kolom er precies
 * daarom bij staat. Verplicht en niet optioneel, net als `dossierdocumenten`: leeg is een geldige
 * stand, ontbreken is iets anders.
 *
 * `CreateRequest` in `apps/store/src/instanties.ts` maakt hem wel optioneel, met een lege lijst
 * als standaardwaarde: dat is de bevroren `/v1`-regel dat een verplicht veld erbij een aanroeper
 * kan breken die het nog niet kent (`apps/store/contract/v1/contract.ts`), en die regel staat los
 * van of er op dit moment een publicatie draait.
 */
export const DossierStates = z.record(Block, z.array(BlockState)).superRefine((states, ctx) => {
  for (const [block, list] of Object.entries(states)) {
    const seen = new Set<string>()
    list.forEach((state, i) => {
      const designation = typeof state === 'string' ? state : state.$stand
      if (seen.has(designation)) {
        ctx.addIssue({
          code: 'custom',
          path: [block, i],
          // Twee standen met dezelfde aanduiding maken "de laatste stand" onbeslisbaar, en dat
          // is precies wat een verwijzing van de lijst leest (R-56).
          message: `blok ${block} noemt stand ${designation} twee keer`,
        })
      }
      seen.add(designation)
    })
  }
})
export type DossierStates = z.infer<typeof DossierStates>

/**
 * De dossierdocumenten: de hele JSON-documenten van bouwblokken eerder in de keten en
 * van het eigen blok, per blok (ontwerp 9.1, U13, U16).
 *
 * **Het eigen blok hoort erbij, en dat stond hier eerst te krap.** R-62 vraagt de
 * antwoorden uit alle eerdere instanties van hetzelfde bouwblok, uit één verwijzing die
 * de reeks oplevert; het voorbeeldbeoordelingsformulier doet dat met een `subgrid` over
 * `criteria` in het contract van zijn eigen blok. Zou dit record alleen eerdere
 * blokken dragen, dan is die eis niet uit te voeren. Wat er nooit in staat is de
 * instantie die nu wordt ingevuld: een volgend uitvraagmoment is een nieuwe instantie
 * (U22, R-196), dus het document van het eigen blok is wat de vorige heeft achtergelaten.
 *
 * Ze komen bij het aanmaken van de instantie mee en worden niet opgehaald: het
 * product kijkt niet zelf in andermans opslag (R-156). Daarmee kan de pod een
 * verwijzing tonen (R-54, R-56) zonder ergens naartoe te hoeven.
 *
 * De sleutel is de blokaanduiding uit de contractregistry, en de waarde is het
 * document zoals dat blok het heeft achtergelaten. Wat erin staat is niet aan dit
 * model: het contract van dat blok beschrijft de vorm (D-37) en die leeft buiten
 * deze definitie. Vandaar een open object en geen uitgeschreven vorm.
 *
 * **Dit veld is de afgeleide van `Dossierstanden` en het blijft één document per blok: de
 * laatste stand.** Besluit 6 van M7 opdracht 9 maakt de lijst standen de bron, en dan is dit
 * wat een verwijzing eruit leest (R-56). Het staat er nog omdat het pad bevroren is: dit veld
 * loopt over `POST /v1/instanties` en over de leesroute van de store, en binnen `/v1` mag er
 * een veld bij en mag er niets verdwijnen of van betekenis veranderen (ontwerp 3.5). Punt 2
 * van die sectie geeft de enige uitweg, en die is hier niet open: er draait sinds 5 september
 * 2026 een publicatie op het nwo-playground-cluster die dit pad gebruikt, en `dossierdocumenten`
 * staat in de bevroren contracttestsuite van de store zowel bij de verplichte invoer als bij de
 * antwoordvelden.
 *
 * **De release-conditie waaronder het wegvalt.** Dit veld gaat weg zodra geen draaiende
 * publicatie het meer leest, precies zoals punt 2 van ontwerp 3.5 dat vraagt: de tabel
 * `publication` is de afnemerslijst, dus de conditie is aantoonbaar en geen inschatting. Tot dan
 * schrijft de store het naast de lijst, met de laatste stand per blok erin, en is het weghalen
 * een eigen wijziging met die aantoning erbij.
 */
export const DossierDocuments = z.record(Block, z.record(z.string(), z.unknown()))
export type DossierDocuments = z.infer<typeof DossierDocuments>

/**
 * De laatste revisie van de directe voorganger, bevroren op de instantie zelf bij het aanmaken
 * (R-201, R-156, M9 opdracht 1).
 *
 * Drie afnemers delen deze ene lezing, elk een ander veld: het zaaien van een nog niet geopende
 * sectie en de teruggaande toetsvelden van de werkverzameling gebruiken `antwoorden`, de regel
 * van de werkverzameling gebruikt `gebeurtenis` (D-59), en het oordeel van R-175 gebruikt `id`
 * als `Oordeel.revisieId`. Vóór deze opdracht loste elke afnemer dit apart en live op bij de
 * voorganger (`apps/runtime/src/zaad.ts`, `apps/runtime/src/werkverzameling.ts`,
 * `apps/runtime/src/oordeel.ts`); na M9 opdracht 3 lezen ze dit veld in plaats daarvan.
 */
export const PredecessorRevisie = z.strictObject({
  id: RevisieId,
  gebeurtenis: Event,
  antwoorden: z.record(FieldId, Answer),
})
export type PredecessorRevisie = z.infer<typeof PredecessorRevisie>

/**
 * De ijkstand: de laatste revisie van de laatste toetsbeurt in de lijn (R-77, R-178, U24, M9
 * opdracht 1).
 *
 * Bij een teruglegging is dat dezelfde instantie als de directe voorganger; bij een hertoets
 * ligt ze verder terug. `antwoorden` is de stand waartegen de werkverzameling het verschil van
 * de invuller toont (`determineDifference`, R-178a); `uitkomsten` zijn de criteriumuitkomsten van de
 * toetslaag van die toetsbeurt, waarmee een toetser bij een hertoets zijn vorige oordeel terugziet
 * (`vorigeUitkomsten`) en de werkverzameling weet welke criteria nog openstaan
 * (`openstaandeCriteria`).
 */
export const BaselineState = z.strictObject({
  antwoorden: z.record(FieldId, Answer),
  uitkomsten: z.array(CriterionOutcome),
})
export type BaselineState = z.infer<typeof BaselineState>

/**
 * Een instantie is één uitgezet formulier op één dossier, gebonden aan één
 * publicatie (R-100, ontwerp 4.5).
 *
 * Een teruglegging maakt geen nieuwe stand in dezelfde instantie maar een nieuwe
 * instantie in dezelfde lijn: een kopie van de gegevens, dezelfde definitie, en
 * een verwijzing naar de voorganger (U21, R-192). Zo is vier stappen terug nog
 * precies te zien hoe er toen is ingediend, en is er nooit iets overschreven.
 *
 * Per dossier is er één lijn, geen tak en geen merge (ontwerp 4.11). Tussen twee
 * beurten wordt nooit samengevoegd (U22, R-196); binnen een beurt voegt Yjs juist
 * wel samen, op tekenniveau (R-72).
 */
export const Instantie = z
  .strictObject({
    id: InstantieId,
    /** De publicatie waaronder deze instantie draait; het dossier blijft daarop staan (R-100, U5). */
    publicatieId: PublicatieId,
    /** Het dossier bij de gastheer. Voor dit product een ondoorzichtige verwijzing. */
    dossierId: DossierId,
    /** Het uitvraagmoment waarop dit formulier is uitgezet; hetzelfde als op de publicatie. */
    uitvraagRef: UitvraagRef,
    /**
     * Wie deze indiening bezit: de aanvrager (R-69).
     *
     * Gezet bij het uitzetten, door de partij die het formulier op het dossier zet, en
     * daarna onveranderlijk. Dat is de reden dat hij hier staat en niet in het token:
     * het eigenaarschap hoort bij de instantie en niet bij een verzoek. Een medeschrijver
     * heeft schrijfrecht uit een deling (R-64) maar dient niet in, en juist dat verschil
     * is niet te maken zonder een benoemde eigenaar.
     *
     * Voor de toetslaag staat hier niemand: die laag hoort bij de rol en niet bij een
     * persoon (R-172b), en het product houdt geen toewijzing bij (R-195).
     */
    eigenaar: Person,
    /** Open of bevroren; bevroren is alleen-lezen (R-86, U21). */
    status: z.enum(['open', 'bevroren']),
    /** Welke kant aan zet is. In de toetsbeurt zijn de invoervelden alleen-lezen (R-193). */
    actieveLaag: Laag,
    /**
     * De einddatum die het formulier bij terugleggen meekrijgt (R-78). Ontbreekt
     * hij, dan is er geen termijn gezet; het product verzint er geen.
     */
    deadline: Moment.optional(),
    /**
     * De vorige instantie in dezelfde lijn, of null bij de eerste (U21, R-192).
     * Null en niet weggelaten, zodat het onderscheid tussen "geen voorganger" en
     * "vergeten mee te geven" zichtbaar blijft.
     */
    voorganger: InstantieId.nullable(),
    /**
     * De laatste revisie van `voorganger`, of null bij de eerste instantie van een lijn. Zie
     * `PredecessorRevisie` hierboven.
     */
    voorgangerRevisie: PredecessorRevisie.nullable(),
    /**
     * De laatste revisie van de laatste toetsbeurt in de lijn, of null zolang er nog geen
     * toetsbeurt is geweest. Zie `BaselineState` hierboven.
     */
    ijkstand: BaselineState.nullable(),
    /**
     * Per bouwblok de standen waarop deze instantie rust, oudste eerst, opgelost tot documenten
     * (R-201, R-62, B4b). Zie `Dossierstanden` hierboven.
     */
    dossierstanden: DossierStates,
    /**
     * Wat er op het dossier al lag toen dit formulier werd uitgezet.
     *
     * Leeg is een geldige stand, maar niet omdat dit het eerste blok in de keten is: ook
     * dat blok kan een document hebben, van de vorige uitvraag op dit dossier (R-62,
     * R-137). Leeg betekent dat er niets ligt, en dan toont een verwijzing dat expliciet
     * in plaats van leeg te blijven (R-61).
     */
    dossierdocumenten: DossierDocuments,
  })
  .superRefine((instantie, ctx) => {
    if (instantie.voorganger === instantie.id) {
      ctx.addIssue({
        code: 'custom',
        path: ['voorganger'],
        // Een keten die naar zichzelf wijst maakt de revisiegeschiedenis van R-95 onleesbaar.
        message: 'een instantie is zijn eigen voorganger niet',
      })
    }
    // `voorgangerRevisie` bestaat precies wanneer `voorganger` bestaat: zonder voorganger is er
    // geen revisie om te bevriezen, en met een voorganger hoort de bevriezing van M9 opdracht 1
    // er altijd te zijn (R-201, R-156).
    if (instantie.voorganger === null && instantie.voorgangerRevisie !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['voorgangerRevisie'],
        message: 'zonder voorganger is er geen revisie om te bevriezen',
      })
    }
    if (instantie.voorganger !== null && instantie.voorgangerRevisie === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['voorgangerRevisie'],
        message: 'een instantie met een voorganger draagt ook diens laatste revisie',
      })
    }
  })

export type Instantie = z.infer<typeof Instantie>
